import {
  resolveGraphqlEndpoint,
  resolveAdminGraphqlEndpoint,
} from "./resolveGraphqlEndpoint";
import { getStoredAccessToken } from "./authStorage";
import { clearSessionAndRedirectToLogin } from "./sessionExpiry";
import {
  refreshSession,
  shouldRedirectAfterRefreshFailure,
} from "./sessionRefresh";

const GQL_ENDPOINT = resolveGraphqlEndpoint();
const ADMIN_GQL_ENDPOINT = resolveAdminGraphqlEndpoint();

type GraphQLError = { message?: string; extensions?: Record<string, unknown> };
type GraphQLResponse<T> = { data?: T; errors?: GraphQLError[] };
type RequestOptions = {
  allowRefresh: boolean;
  forbiddenMessage?: string;
};

function isSessionExpiryCode(code: unknown): boolean {
  return code === "UNAUTHENTICATED" || code === "TOKEN_EXPIRED";
}

async function postGraphql(
  endpoint: string,
  query: string,
  variables: Record<string, unknown>,
): Promise<Response> {
  const token = getStoredAccessToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  return await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify({ query, variables }),
  });
}

async function retryAfterSessionRefresh<T>(
  endpoint: string,
  query: string,
  variables: Record<string, unknown>,
  options: RequestOptions,
): Promise<T> {
  const refreshResult = await refreshSession();
  if (refreshResult.ok && !refreshResult.skipped) {
    return await requestGraphql<T>(endpoint, query, variables, {
      ...options,
      allowRefresh: false,
    });
  }

  if (shouldRedirectAfterRefreshFailure(refreshResult)) {
    clearSessionAndRedirectToLogin();
  }

  if ("message" in refreshResult && refreshResult.message) {
    throw new Error(refreshResult.message);
  }

  throw new Error("Session expired");
}

async function requestGraphql<T>(
  endpoint: string,
  query: string,
  variables: Record<string, unknown>,
  options: RequestOptions,
): Promise<T> {
  const res = await postGraphql(endpoint, query, variables);

  if (res.status === 401) {
    if (options.allowRefresh) {
      return await retryAfterSessionRefresh<T>(
        endpoint,
        query,
        variables,
        options,
      );
    }
    clearSessionAndRedirectToLogin();
    throw new Error("Session expired");
  }

  if (res.status === 403 && options.forbiddenMessage) {
    throw new Error(options.forbiddenMessage);
  }

  let json: GraphQLResponse<T> | undefined;
  try {
    json = (await res.json()) as GraphQLResponse<T>;
  } catch (err) {
    if (res.ok) throw err;
  }
  if (!res.ok && !json?.errors?.length) {
    throw new Error(`HTTP ${res.status}: ${res.statusText}`);
  }

  if (json?.errors?.length) {
    const first = json.errors[0] || {};
    const code = (first.extensions as { code?: string } | undefined)?.code;
    if (isSessionExpiryCode(code)) {
      if (options.allowRefresh) {
        return await retryAfterSessionRefresh<T>(
          endpoint,
          query,
          variables,
          options,
        );
      }
      clearSessionAndRedirectToLogin();
      throw new Error("Session expired");
    }
    const message = first.message || "GraphQL request failed";
    const e = new Error(message) as Error & {
      code?: unknown;
      extensions?: unknown;
    };
    e.code = first.extensions?.code;
    e.extensions = first.extensions;
    throw e;
  }

  return json?.data as T;
}

export async function gqlRequest<T = any>(
  query: string,
  variables: Record<string, unknown> = {},
): Promise<T> {
  return await requestGraphql<T>(GQL_ENDPOINT, query, variables, {
    allowRefresh: true,
  });
}

export async function adminGqlRequest<T = any>(
  query: string,
  variables: Record<string, unknown> = {},
): Promise<T> {
  return await requestGraphql<T>(ADMIN_GQL_ENDPOINT, query, variables, {
    allowRefresh: true,
    forbiddenMessage: "Insufficient permissions",
  });
}
